import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Radio,
  alpha,
  Icon,
  IconButton,
  Divider,
  CircularProgress,
} from "@mui/material";
import { ExportFormat, ITableFilters } from "../app.interface";
import { exportData } from "../utils/export";
import { TableColumn } from "../app.interface";
import { publicService } from "../services/public.service";
import { toast } from "react-toastify";

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  formats: ExportFormat[];
  totalItems: number;
  selectedItems: number[];
  data: Record<string, any>[];
  columns: TableColumn[];
  filename?: string;
  datasource?: string;
  formatter?: (data: any) => any;
  filters?: ITableFilters[];
}

interface FormatOption {
  format: ExportFormat;
  title: string;
  description: string;
  icon: string;
  color: string;
}

const formatOptions: FormatOption[] = [
  {
    format: "csv",
    title: "CSV File",
    description:
      "Simple, universal format for data analysis and compatibility.",
    icon: "format_list_bulleted",
    color: "#10B981",
  },
  {
    format: "excel",
    title: "Excel Spreadsheet",
    description: "Rich formatting with formulas and multiple sheets support.",
    icon: "table_chart",
    color: "#6366F1",
  },
  {
    format: "json",
    title: "JSON Data",
    description: "Developer-friendly format for data integration and APIs.",
    icon: "data_object",
    color: "#F59E0B",
  },
];

export const AppExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  formats,
  totalItems,
  selectedItems,
  data,
  columns,
  filename = "table-export",
  datasource,
  formatter,
  filters,
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>("csv");
  const [exportScope, setExportScope] = useState<"all" | "selected">("all");
  const [isExporting, setIsExporting] = useState(false);

  const handleClose = () => {
    setSelectedFormat("csv");
    setExportScope("all");
    onClose();
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);

      let exportedData = [];
      if (exportScope === "all") {
        if (datasource) {
          exportedData = await publicService.exportData(datasource, filters);
        }
      } else {
        exportedData = data.filter((_, index) => selectedItems.includes(index));
      }

      if (exportedData.length === 0) {
        toast.error(
          "No data to export. Please adjust your filters or selection and try again.",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
        return;
      } else {
        if (formatter) {
          exportedData = formatter(exportedData);
        }

        await exportData({
          data: exportedData,
          columns,
          filename,
          format: selectedFormat,
        });

        // Show success notification
        toast.success(
          `Successfully exported ${
            exportedData.length
          } records as ${selectedFormat.toUpperCase()}`,
          {
            position: "top-right",
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }

      handleClose();
    } catch (error: any) {
      console.error("Export failed:", error);

      // Provide detailed error feedback to the user
      let errorMessage = "Export failed. Please try again.";

      if (error?.message) {
        if (
          error.message.includes("Network Error") ||
          error.message.includes("fetch")
        ) {
          errorMessage =
            "Export failed due to network issues. Please check your connection and try again.";
        } else if (error.message.includes("timeout")) {
          errorMessage =
            "Export timed out. Try exporting fewer records or check your connection.";
        } else if (
          error.message.includes("401") ||
          error.message.includes("unauthorized")
        ) {
          errorMessage =
            "Export failed due to authentication issues. Please log in again.";
        } else if (
          error.message.includes("403") ||
          error.message.includes("forbidden")
        ) {
          errorMessage =
            "You don't have permission to export this data. Contact your administrator.";
        } else if (error.message.includes("500")) {
          errorMessage =
            "Server error occurred during export. Please try again later or contact support.";
        } else {
          errorMessage = `Export failed: ${error.message}`;
        }
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 6000, // Show for 6 seconds to give user time to read
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon sx={{ fontSize: 20 }}>download</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              Export Table Data
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box my={3}>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Icon sx={{ fontSize: 18 }}>description</Icon>
            Export Format
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            gap={2}
          >
            {formatOptions
              .filter((option) => formats.includes(option.format))
              .map((option) => (
                <Box
                  key={option.format}
                  onClick={() => setSelectedFormat(option.format)}
                  sx={{
                    position: "relative",
                    p: 2,
                    borderRadius: 1,
                    cursor: "pointer",
                    border: "1px solid",
                    borderColor:
                      selectedFormat === option.format
                        ? option.color
                        : "#E5E7EB",
                    bgcolor:
                      selectedFormat === option.format
                        ? alpha(option.color, 0.04)
                        : "#FFFFFF",
                    transition: "all 0.2s ease-out",
                    "&:hover": {
                      borderColor: option.color,
                      transform: "translateY(-1px)",
                      boxShadow: `0 4px 12px ${alpha(option.color, 0.12)}`,
                    },
                  }}
                >
                  <Box display="flex" gap={2} alignItems="flex-start">
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: alpha(option.color, 0.1),
                        color: option.color,
                        display: "flex",
                      }}
                    >
                      <Icon sx={{ fontSize: 20 }}>{option.icon}</Icon>
                    </Box>
                    <Box flex={1}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        gutterBottom
                      >
                        {option.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.4 }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                    <Radio
                      checked={selectedFormat === option.format}
                      sx={{
                        p: 0.5,
                        color: alpha(option.color, 0.3),
                        "&.Mui-checked": {
                          color: option.color,
                        },
                      }}
                    />
                  </Box>
                </Box>
              ))}
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}
          >
            <Icon sx={{ fontSize: 18 }}>filter_list</Icon>
            Data Range
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns="repeat(auto-fit, minmax(250px, 1fr))"
            gap={2}
          >
            <Box
              onClick={() => setExportScope("all")}
              sx={{
                position: "relative",
                p: 2,
                borderRadius: 1,
                cursor: "pointer",
                border: "1px solid",
                borderColor: exportScope === "all" ? "#0891B2" : "#E5E7EB",
                bgcolor:
                  exportScope === "all" ? alpha("#0891B2", 0.04) : "#FFFFFF",
                transition: "all 0.2s ease-out",
                "&:hover": {
                  borderColor: "#0891B2",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(8, 145, 178, 0.12)",
                },
              }}
            >
              <Box display="flex" gap={2} alignItems="flex-start">
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: alpha("#0891B2", 0.1),
                    color: "#0891B2",
                    display: "flex",
                  }}
                >
                  <Icon sx={{ fontSize: 20 }}>all_inclusive</Icon>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    All Data
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4 }}
                  >
                    Export all {totalItems} items
                  </Typography>
                </Box>
                <Radio
                  checked={exportScope === "all"}
                  sx={{
                    p: 0.5,
                    color: alpha("#0891B2", 0.3),
                    "&.Mui-checked": {
                      color: "#0891B2",
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              onClick={() => setExportScope("selected")}
              sx={{
                position: "relative",
                p: 2,
                borderRadius: 1,
                cursor: "pointer",
                border: "1px solid",
                borderColor: exportScope === "selected" ? "#0891B2" : "#E5E7EB",
                bgcolor:
                  exportScope === "selected"
                    ? alpha("#0891B2", 0.04)
                    : "#FFFFFF",
                transition: "all 0.2s ease-out",
                "&:hover": {
                  borderColor: "#0891B2",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(8, 145, 178, 0.12)",
                },
                opacity: selectedItems.length === 0 ? 0.5 : 1,
                pointerEvents: selectedItems.length === 0 ? "none" : "auto",
              }}
            >
              <Box display="flex" gap={2} alignItems="flex-start">
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    bgcolor: alpha("#0891B2", 0.1),
                    color: "#0891B2",
                    display: "flex",
                  }}
                >
                  <Icon sx={{ fontSize: 20 }}>checklist</Icon>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Selected Only
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1.4 }}
                  >
                    Export {selectedItems.length} selected items
                  </Typography>
                </Box>
                <Radio
                  checked={exportScope === "selected"}
                  disabled={selectedItems.length === 0}
                  sx={{
                    p: 0.5,
                    color: alpha("#0891B2", 0.3),
                    "&.Mui-checked": {
                      color: "#0891B2",
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          bgcolor: "#F8FAFC",
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          startIcon={<Icon>close</Icon>}
          disabled={isExporting}
          sx={{
            borderRadius: 1,
            textTransform: "none",
            px: 2,
            py: 1,
            borderColor: "#E5E7EB",
            color: "#64748B",
            "&:hover": {
              borderColor: "#94A3B8",
              bgcolor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={isExporting}
          startIcon={
            isExporting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <Icon>download</Icon>
            )
          }
          sx={{
            borderRadius: 1,
            textTransform: "none",
            px: 3,
            py: 1,
            bgcolor: "#0891B2",
            "&:hover": {
              bgcolor: "#0E7490",
            },
            "&.Mui-disabled": {
              bgcolor: alpha("#0891B2", 0.6),
              color: "white",
            },
          }}
        >
          {isExporting ? "Exporting..." : "Export Data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
